import { DEFAULT_THEME, type ReportTheme } from '../types/reportTheme';
import type { ReportTemplate } from '../types/reportTemplate';
import { getThemePreset } from './reportThemePresets';

/** Merge template-local theme overrides onto the base report theme. */
export function mergeTemplateTheme(
  base: ReportTheme,
  template?: ReportTemplate | null,
): ReportTheme {
  if (!template) return base;
  const partial = template.theme;
  const hasThemePartial = partial != null && Object.keys(partial).length > 0;
  const coverLayout = template.coverLayout;
  if (!hasThemePartial && !coverLayout) return base;
  const merged: ReportTheme = {
    ...base,
    ...(partial ?? {}),
    ...(coverLayout ? { coverLayout } : {}),
  };
  // A spread is shallow: the array/object-valued fields still alias `base` (or
  // the partial). Clone them so an in-place mutation downstream can't reach back
  // and corrupt the base/store theme through the merged result.
  if (Array.isArray(merged.chartPalette)) merged.chartPalette = [...merged.chartPalette];
  if (merged.disclaimerByArchetype) merged.disclaimerByArchetype = { ...merged.disclaimerByArchetype };
  if (merged.themeLocale) merged.themeLocale = { ...merged.themeLocale };
  return merged;
}

/**
 * Brand-identity fields that a PRESET must never overwrite. A preset is a
 * design system (palette + typography + figure framing); a tenant's company
 * name and logo are identity that survive any preset selection.
 */
const PRESET_PROTECTED_BRAND_FIELDS = ['companyName', 'logoUrl'] as const;

/**
 * Strip the protected brand fields out of an applied preset partial so the
 * preset can never clobber `companyName` / `logoUrl`. (The curated presets in
 * `THEME_PRESETS` already omit these — this is a defensive guard so a future
 * preset edit can't silently break the contract.)
 */
function stripBrandFromPreset(
  preset: Partial<ReportTheme>,
): Partial<ReportTheme> {
  const clone: Partial<ReportTheme> = { ...preset };
  for (const field of PRESET_PROTECTED_BRAND_FIELDS) {
    delete clone[field];
  }
  return clone;
}

/**
 * Brand-identity fields the store/global theme may contribute over a preset.
 *
 * GOLDEN LOCK (William 2026-06-29): with Template Studio retired and the design
 * locked to the golden `memo` preset, the store/global theme contributes ONLY
 * per-company BRAND identity — name, logo, primary/accent colour, footer copy.
 * DESIGN fields (fonts, palette, density, columns, heading/body/background
 * colours, table style…) are owned by the golden preset and must NOT be
 * overridden by a tenant's legacy Studio Theme-tab customizations, or the
 * locked golden look would drift per tenant.
 */
const BRAND_DELTA_FIELDS = [
  'companyName',
  'logoUrl',
  'primaryColor',
  'sidebarText',
  'footerText',
  'footerSubtext',
  'footerEmail',
  // The CONFIDENTIAL/security overlay is identity, not design — it must survive
  // the locked golden design (a confidential report must keep its stamp).
  'themeWatermark',
] as const;

/**
 * Compute the store/global theme's GENUINE BRAND overrides — the brand-identity
 * fields (see `BRAND_DELTA_FIELDS`) where it diverges from `DEFAULT_THEME`. A
 * brand field left at its DEFAULT value is "unset" (the golden preset shows
 * through); a brand field the tenant explicitly changed is a real choice that
 * rides over the preset. Non-brand DESIGN fields are intentionally ignored so
 * the golden design stays fixed across tenants (the locked-design contract).
 */
function storeDeltaFromDefault(base: ReportTheme): Partial<ReportTheme> {
  const delta: Partial<ReportTheme> = {};
  for (const key of BRAND_DELTA_FIELDS) {
    // `!== undefined` guards a partial base (a brand field absent from `base`
    // must not inject `undefined` over the preset); `!== DEFAULT` keeps the
    // "left at the floor ⇒ unset" semantics.
    if (base[key] !== undefined && base[key] !== DEFAULT_THEME[key]) {
      // Narrowing the heterogeneous value back onto the partial is safe — the
      // key/value come from the same ReportTheme instance.
      (delta as Record<string, unknown>)[key] = base[key];
    }
  }
  return delta;
}

/**
 * resolveEffectiveTheme — the single theme-resolution seam.
 *
 * Precedence, lowest → highest (later wins on a per-field basis):
 *   1. DEFAULT_THEME.
 *   2. THEME_PRESETS[template.themePreset]  (brand fields stripped).
 *   3. store / global theme — only its GENUINE overrides (delta from
 *      DEFAULT_THEME). Brand fields (companyName/logoUrl) always come from
 *      here, never from the preset.
 *   4. template.theme  (per-template editor overrides) + template.coverLayout.
 *   5. report overrides (per-report, highest).
 *
 * CRITICAL invariants:
 *   - A field the USER set in the template editor (template.theme) ALWAYS
 *     wins over the preset, because step 4 is applied AFTER step 2.
 *   - A field the store/tenant genuinely customised wins over the preset
 *     (step 3 over step 2); a field the store left at the DEFAULT floor lets
 *     the preset show through.
 *   - `companyName` / `logoUrl` are NEVER sourced from a preset (step 2 is
 *     brand-stripped); they come from `base` (or template/report overrides).
 *   - When `template.themePreset` is ABSENT, this reduces EXACTLY to
 *     `mergeTemplateTheme(base, template)` (then report overrides), so legacy
 *     reports are byte-stable.
 */
export function resolveEffectiveTheme(args: {
  base: ReportTheme;
  template?: ReportTemplate | null;
  report?: Partial<ReportTheme> | null;
}): ReportTheme {
  const { base, template, report } = args;

  const presetId = template?.themePreset;
  const rawPreset = getThemePreset(presetId);

  // Fast path: no preset → preserve the exact legacy behaviour, then layer
  // report overrides on top. Keeps absence-of-preset reports byte-stable.
  if (!rawPreset) {
    const merged = mergeTemplateTheme(base, template);
    if (!report || Object.keys(report).length === 0) return merged;
    return { ...merged, ...report };
  }

  const preset = stripBrandFromPreset(rawPreset);
  const storeDelta = storeDeltaFromDefault(base);

  // steps 1 + 2 + 3 — DEFAULT_THEME floor, preset on top, then the store's
  // genuine overrides. The preset shows through for any field the store left
  // at the DEFAULT floor; real store choices (including brand) win over it.
  const presetUnderStore: ReportTheme = {
    ...DEFAULT_THEME,
    ...preset,
    ...storeDelta,
  };

  // step 4 — template-editor fields + coverLayout win over preset + store.
  const templatePartial = template?.theme ?? {};
  const coverLayout = template?.coverLayout;
  const withTemplate: ReportTheme = {
    ...presetUnderStore,
    ...templatePartial,
    ...(coverLayout ? { coverLayout } : {}),
  };

  // step 5 — per-report overrides win over everything.
  if (!report || Object.keys(report).length === 0) return withTemplate;
  return { ...withTemplate, ...report };
}
